import { useEffect, useState } from "react";
import { Bell, CreditCard, Edit3, Plus, RefreshCw, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";

const TIME_ZONE = "America/Cuiaba";
const money = (value) => Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const onlyNumbers = (value = "") => value.toString().replace(/\D/g, "");

const maskDateBR = (value = "") => {
  const digits = onlyNumbers(value).slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const brToIsoDate = (value = "") => {
  const match = value.toString().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return "";
  const [, day, month, year] = match;
  return `${year}-${month}-${day}`;
};

const isoToBrDate = (value = "") => {
  const match = value.toString().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return maskDateBR(value);
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

const normalizeDateForSave = (value = "") => {
  const text = value.toString().trim();
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const br = maskDateBR(text);
  return brToIsoDate(br) || text;
};

const getCuiabaTodayIso = () => {
  const parts = new Intl.DateTimeFormat("pt-BR", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const day = parts.find((p) => p.type === "day")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  const year = parts.find((p) => p.type === "year")?.value;
  return `${year}-${month}-${day}`;
};

const formatDateBR = (value) => {
  if (!value) return "Não informado";
  const text = value.toString().trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(text)) return text;
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return isoToBrDate(text);
  return maskDateBR(text) || "Não informado";
};

const Field = ({ label, value, onChange, type = "text", textarea = false, rows = 2, placeholder = "" }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</label>
    {textarea ? (
      <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full mt-1 px-3 py-2 text-sm bg-secondary/30 rounded-lg outline-none focus:bg-white border border-transparent focus:border-primary resize-none" />
    ) : (
      <input type={type} value={value || ""} onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} placeholder={placeholder} className="w-full mt-1 px-3 py-2 text-sm bg-secondary/30 rounded-lg outline-none focus:bg-white border border-transparent focus:border-primary" />
    )}
  </div>
);

const DateField = ({ label, value, onChange }) => {
  const displayValue = formatDateBR(value) === "Não informado" ? "" : formatDateBR(value);

  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</label>
      <input
        type="text"
        inputMode="numeric"
        value={displayValue}
        onChange={(e) => {
          const masked = maskDateBR(e.target.value);
          onChange(brToIsoDate(masked) || masked);
        }}
        placeholder="dd/mm/aaaa"
        className="w-full mt-1 px-3 py-2 text-sm bg-secondary/30 rounded-lg outline-none focus:bg-white border border-transparent focus:border-primary"
      />
      <div className="text-[11px] text-muted-foreground mt-1">Formato: dd/mm/aaaa · Fuso: Cuiabá-MT</div>
    </div>
  );
};

const SelectField = ({ label, value, onChange, options }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</label>
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full mt-1 px-3 py-2 text-sm bg-secondary/30 rounded-lg outline-none focus:bg-white border border-transparent focus:border-primary">
      {options.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  </div>
);

const emptyExpense = {
  name: "",
  company: "",
  payment_type: "Pix",
  status: "Pendente",
  payment_date: "",
  due_date: "",
  reminder_date: "",
  has_reminder: false,
  installments: "",
  total_value: "",
  monthly_installment: "",
  notes: "",
};

const normalizeExpense = (item = {}) => {
  const dueDate = normalizeDateForSave(item.due_date || (item.day && item.month && item.year ? `${String(item.day).padStart(2, "0")}/${String(item.month).padStart(2, "0")}/${item.year}` : ""));
  const reminderDate = normalizeDateForSave(item.reminder_date || "");
  const status = item.status || "Pendente";

  return {
    ...emptyExpense,
    ...item,
    payment_type: item.payment_type || item.payment_method || "Pix",
    status,
    total_value: item.total_value ?? item.value ?? "",
    monthly_installment: item.monthly_installment ?? item.value ?? "",
    payment_date: normalizeDateForSave(item.payment_date || ""),
    reminder_date: reminderDate,
    due_date: dueDate,
    has_reminder: Boolean(item.has_reminder || reminderDate || dueDate),
  };
};

const buildFinancePayload = (list) => list.map((item) => {
  const normalized = normalizeExpense(item);
  const hasReminder = Boolean(normalized.has_reminder || normalized.reminder_date || normalized.due_date);

  return {
    ...normalized,
    has_reminder: hasReminder,
    payment_date: normalizeDateForSave(normalized.payment_date),
    due_date: hasReminder ? normalizeDateForSave(normalized.due_date) : "",
    reminder_date: hasReminder ? normalizeDateForSave(normalized.reminder_date) : "",
    value: normalized.monthly_installment || normalized.total_value || normalized.value || 0,
    timezone: TIME_ZONE,
  };
});

const FinanceEditor = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance");
      setItems((data?.fixed_expenses || []).map(normalizeExpense));
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Erro ao carregar financeiro");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const persist = async (list, successMessage = "Financeiro salvo!") => {
    const payload = buildFinancePayload(list);
    await api.put("/finance", { fixed_expenses: payload });
    setItems(payload.map(normalizeExpense));
    toast.success(successMessage);
  };

  const saveFinance = async () => {
    setSaving(true);
    try {
      await persist(items, "Financeiro salvo!");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Erro ao salvar financeiro");
    } finally {
      setSaving(false);
    }
  };

  const add = () => {
    const next = [...items, { ...emptyExpense }];
    setItems(next);
    setEditing((current) => ({ ...current, [next.length - 1]: true }));
  };

  const update = (index, key, value) => {
    setItems(items.map((item, currentIndex) => {
      if (currentIndex !== index) return item;
      const next = { ...item, [key]: value };

      if (key === "payment_type" && value !== "Crédito") {
        next.installments = "";
      }

      if ((key === "total_value" || key === "installments") && next.payment_type === "Crédito") {
        const total = Number(key === "total_value" ? value : next.total_value || 0);
        const installments = Number(key === "installments" ? value : next.installments || 0);
        if (total > 0 && installments > 0) next.monthly_installment = Number((total / installments).toFixed(2));
      }

      if (key === "status" && value === "Pago" && !next.payment_date) {
        next.payment_date = getCuiabaTodayIso();
      }

      if (key === "status" && value === "A vencer") {
        next.has_reminder = true;
      }

      return next;
    }));
  };

  const remove = async (index) => {
    const item = items[index];
    const label = item?.name || `Despesa #${index + 1}`;
    if (!window.confirm(`Remover "${label}"?`)) return;

    const next = items.filter((_, currentIndex) => currentIndex !== index);
    setItems(next);
    setEditing((current) => {
      const updated = {};
      Object.entries(current).forEach(([key, value]) => {
        const numericKey = Number(key);
        if (numericKey < index) updated[numericKey] = value;
        if (numericKey > index) updated[numericKey - 1] = value;
      });
      return updated;
    });

    try {
      await persist(next, "Despesa removida!");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Erro ao remover despesa");
      setItems(items);
    }
  };

  const total = items.reduce((sum, item) => sum + Number(item.total_value || item.value || 0), 0);
  const monthlyTotal = items.reduce((sum, item) => sum + Number(item.monthly_installment || item.value || item.total_value || 0), 0);

  if (loading) return <div className="text-muted-foreground">Carregando financeiro…</div>;

  return (
    <div className="space-y-6" data-testid="admin-finance">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl text-primary">Financeiro</h2>
          <p className="text-sm text-muted-foreground mt-1">Cadastre despesas fixas com datas em pt-BR, lembretes e fuso de Cuiabá-MT.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={load} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-white text-sm font-medium hover:bg-secondary/40">
            <RefreshCw className="w-4 h-4" /> Atualizar
          </button>
          <button type="button" onClick={saveFinance} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent text-accent-foreground text-sm font-medium hover:bg-accent/90 disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar financeiro"}
          </button>
          <a href="/painel" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">
            Abrir painel financeiro
          </a>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white border border-border rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Despesas cadastradas</div>
          <div className="font-display text-3xl text-primary font-semibold mt-2">{items.length}</div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Valor total</div>
          <div className="font-display text-3xl text-primary font-semibold mt-2">{money(total)}</div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent text-accent-foreground flex items-center justify-center"><Bell className="w-5 h-5" /></div>
          <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Mensal previsto</div><div className="text-sm text-primary font-medium">{money(monthlyTotal)}</div></div>
        </div>
      </div>

      <div className="space-y-4">
        {items.map((item, index) => {
          const isEditing = Boolean(editing[index]);
          const showCreditFields = item.payment_type === "Crédito";
          const showPaidFields = item.status === "Pago";
          const hasReminder = Boolean(item.has_reminder || item.reminder_date || item.due_date || item.status === "A vencer");

          return (
            <div key={index} className="bg-white rounded-2xl border border-border p-5 space-y-4">
              <div className="flex flex-wrap justify-between items-center gap-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Despesa #{index + 1}</div>
                  <div className="font-display text-lg text-primary font-semibold">{item.name || "Nova despesa"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setEditing((current) => ({ ...current, [index]: !current[index] }))} className="text-primary hover:text-accent text-xs flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-border hover:border-accent transition">
                    <Edit3 className="w-3 h-3" /> {isEditing ? "Concluir" : "Editar"}
                  </button>
                  <button type="button" onClick={() => remove(index)} className="text-destructive hover:text-destructive/80 text-xs flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-border">
                    <Trash2 className="w-3 h-3" /> Remover
                  </button>
                </div>
              </div>

              {isEditing ? (
                <div className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Nome da despesa" value={item.name} onChange={(value) => update(index, "name", value)} />
                    <Field label="Empresa" value={item.company} onChange={(value) => update(index, "company", value)} />
                    <SelectField label="Tipo de pagamento" value={item.payment_type} onChange={(value) => update(index, "payment_type", value)} options={[
                      { value: "Pix", label: "Pix" },
                      { value: "Crédito", label: "Crédito" },
                      { value: "Débito", label: "Débito" },
                      { value: "Boleto", label: "Boleto" },
                    ]} />
                    <SelectField label="Status" value={item.status} onChange={(value) => update(index, "status", value)} options={[
                      { value: "Pago", label: "Pago" },
                      { value: "Pendente", label: "Pendente" },
                      { value: "A vencer", label: "A vencer" },
                    ]} />
                    <Field label="Valor total" type="number" value={item.total_value} onChange={(value) => update(index, "total_value", value)} />
                    <Field label="Valor da parcela mensal" type="number" value={item.monthly_installment} onChange={(value) => update(index, "monthly_installment", value)} />
                  </div>

                  {showCreditFields && (
                    <div className="grid md:grid-cols-2 gap-4 p-4 rounded-2xl bg-secondary/30 border border-border/60">
                      <div className="md:col-span-2 flex items-center gap-2 text-sm font-semibold text-primary"><CreditCard className="w-4 h-4" /> Dados do cartão de crédito</div>
                      <Field label="Quantidade de parcelas" type="number" value={item.installments} onChange={(value) => update(index, "installments", value)} />
                      <Field label="Valor da parcela mensal" type="number" value={item.monthly_installment} onChange={(value) => update(index, "monthly_installment", value)} />
                    </div>
                  )}

                  <div className="p-4 rounded-2xl bg-secondary/30 border border-border/60 space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-primary">Lembrete de pagamento</div>
                        <div className="text-xs text-muted-foreground">Use para despesas pendentes ou a vencer. Datas em dd/mm/aaaa.</div>
                      </div>
                      {!showPaidFields && !hasReminder && (
                        <button type="button" onClick={() => update(index, "has_reminder", true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90">
                          <Bell className="w-4 h-4" /> Cadastrar lembrete
                        </button>
                      )}
                      {!showPaidFields && hasReminder && (
                        <button type="button" onClick={() => {
                          update(index, "has_reminder", false);
                          update(index, "reminder_date", "");
                        }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-white text-xs font-medium hover:bg-secondary/40">
                          Remover lembrete
                        </button>
                      )}
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      {showPaidFields && <DateField label="Data de pagamento" value={item.payment_date} onChange={(value) => update(index, "payment_date", value)} />}
                      {!showPaidFields && hasReminder && <DateField label="Data que deve ser efetuado o pagamento" value={item.due_date} onChange={(value) => update(index, "due_date", value)} />}
                      {!showPaidFields && hasReminder && <DateField label="Data do lembrete" value={item.reminder_date} onChange={(value) => update(index, "reminder_date", value)} />}
                    </div>
                  </div>

                  <Field label="Observações" textarea rows={3} value={item.notes} onChange={(value) => update(index, "notes", value)} placeholder="Exemplo: contrato, recorrência, número do boleto, fornecedor..." />
                </div>
              ) : (
                <div className="grid md:grid-cols-4 gap-3">
                  <div className="bg-secondary/30 rounded-xl p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Empresa</div><div className="text-sm font-medium text-primary">{item.company || "Não informado"}</div></div>
                  <div className="bg-secondary/30 rounded-xl p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Pagamento</div><div className="text-sm font-medium text-primary">{item.payment_type || "Não informado"}</div></div>
                  <div className="bg-secondary/30 rounded-xl p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Status</div><div className="text-sm font-medium text-primary">{item.status || "Pendente"}</div></div>
                  <div className="bg-secondary/30 rounded-xl p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Valor total</div><div className="text-sm font-medium text-primary">{money(item.total_value || item.value)}</div></div>
                  <div className="bg-secondary/30 rounded-xl p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Parcela mensal</div><div className="text-sm font-medium text-primary">{money(item.monthly_installment || item.value || item.total_value)}</div></div>
                  <div className="bg-secondary/30 rounded-xl p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Parcelas</div><div className="text-sm font-medium text-primary">{item.payment_type === "Crédito" ? item.installments || "Não informado" : "Não se aplica"}</div></div>
                  <div className="bg-secondary/30 rounded-xl p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Vencimento/Pagamento</div><div className="text-sm font-medium text-primary">{formatDateBR(item.due_date || item.payment_date)}</div></div>
                  <div className="bg-secondary/30 rounded-xl p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Lembrete</div><div className="text-sm font-medium text-primary">{formatDateBR(item.reminder_date)}</div></div>
                </div>
              )}
            </div>
          );
        })}

        <button type="button" onClick={add} className="w-full py-3 rounded-xl border-2 border-dashed border-border text-sm text-muted-foreground hover:border-primary hover:text-primary flex items-center justify-center gap-2">
          <Plus className="w-4 h-4" /> Cadastrar despesa
        </button>
      </div>
    </div>
  );
};

export default FinanceEditor;
